import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
} from "@angular/core";
import { Article } from "src/app/interfaces/article";
import * as moment from "moment";
import { TranslateService } from "@ngx-translate/core";
import { WeekDay } from "@angular/common";
import { ApplicationService } from "src/app/services/application.service";
import { CompanyGroupLayout } from "src/app/interfaces/company-group-layout";
import { CompanyGroup } from "src/app/interfaces/company-group";
import { SettingsService } from "src/app/services/settings.service";

@Component({
    selector: "app-select-week",
    templateUrl: "./select-week.component.html",
    styleUrls: ["./select-week.component.scss"],
    standalone: false
})
export class SelectWeekComponent implements OnInit {
  @Input() article: Article;
  @Input() deliveryWeek: string;
  @Input() type = "dropdown";
  @Input() showAsap: boolean;
  @Output() weekSelected: EventEmitter<any> = new EventEmitter<any>();
  weeks: any = [];
  monthList: any = [];
  currentDate: Date;
  stockMonth: number;
  @Input() triggerUpdate = true;
  lockedWeeksBikes: boolean;
  lockedWeeksParts: boolean;
  lockedWeeks: boolean;
  hasAsapDelivery = this.applicationService.getSelectCompanyGroupCode() === 'WG';
  
  // "Or before" feature
  showOrBeforeOption = false;
  orBeforeSelected = false;
  orBeforeWeeksList: any[] = [];
  selectedOrBeforeWeek: string = '';

  constructor(
    private translateService: TranslateService,
    public applicationService: ApplicationService,
    private settingsService: SettingsService
  ) {
    this.currentDate = new Date();
  }

  ngOnInit() {
    this.renderWeeks();
    this.getCompanyGroupSettings();
    this.checkOrBeforeOption();
    this.checkDeliveryWeekBeforeList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["deliveryWeek"]
      
    ) {
      if (this.applicationService.getSelectCompanyGroupCode() !== "WG" ) {
        this.renderWeeks();
      }

      this.checkDeliveryWeekBeforeList();

      if (!this.showOrBeforeOption) {
        this.article.asap_delivery = true;
      }
    }
  }

  checkDeliveryWeekBeforeList() {
    if (!this.deliveryWeek || this.monthList.length === 0 || !this.monthList[0][1]?.length) {
      return;
    }
    const firstWeekValue = this.monthList[0][1][0].value;
    if (parseInt(this.deliveryWeek, 10) < parseInt(firstWeekValue, 10)) {
      this.showOrBeforeOption = true;
      this.generateOrBeforeWeeksList();
      this.orBeforeSelected = true;
      this.selectedOrBeforeWeek = this.deliveryWeek;
    }
  }

  checkOrBeforeOption() {
    // Only show "or before" option for bicycles and when enabled in settings
    const b2bSettings = this.settingsService.getB2bLocalSettings(
      this.applicationService.getSelectCompanyGroupCode()
    );
    this.showOrBeforeOption = 
      b2bSettings?.ordering?.show_or_before_week_option === true && 
      this.article?.article_type !== 'P';
  }

  getCompanyGroupSettings() {
    const companyGroupSettings = this.applicationService.getSettingsForCompanyGroup(
      this.applicationService.getSelectCompanyGroupCode()
    ) as CompanyGroup;
    this.lockedWeeks = (companyGroupSettings.locked_delivery_week_bikes && this.article.article_type !== "P") || (companyGroupSettings.locked_delivery_week_parts  && this.article.article_type === "P") || companyGroupSettings.locked_delivery_week_all;

    // LAP only for 2024 models
    if (this.applicationService.getSelectCompanyGroupCode() === "LAP" && this.article.season !== "2024") {
      this.lockedWeeks = true;
    }
  }

  renderWeeks() {
    let monthList = {};
    this.monthList = [];
    if (this.article.stock_status < 4 && this.article.single_article_code) {
      const currentYear = moment().year();

      if (this.article.stock_year < currentYear) {
        this.article.stock_year = currentYear;
        this.article.stock_week = moment().isoWeek();
      }

      const canBeOrderedThisYear = currentYear === this.article.stock_year;

      // Add all remaining weeks from the current year
      monthList = Object.assign(
        monthList,
        this.addYear(
          canBeOrderedThisYear
            ? this.type === "dropdown"
              ? this.article.stock_week
              : moment().isoWeek()
            : this.article.stock_week,
          0,
          null,
          currentYear + 1
        )
      );

      // ANL - LAP has a max order date
      // before 31-08-YYYY max week 52 current year
      // after 31-08-YYYY max week 52 next year
      if (this.applicationService.getSelectCompanyGroupCode() === "ANL" || this.applicationService.getSelectCompanyGroupCode() === "LAP") {
        const today = moment().set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
        const onlyOrderThisYearDate = moment().set({
          month: 7,
          date: 31,
        });

        if (today.isAfter(onlyOrderThisYearDate)) {
          this.monthList = this.addYear(1, 1, monthList, currentYear + 1);
        } else if (this.article.stock_year > currentYear) {
          monthList = {};
          const stockweek = String(this.article.stock_week).length < 2
          ? "0" + this.article.stock_week
          : this.article.stock_week;

          if (this.deliveryWeek !== `${this.article.stock_year}${stockweek}`)
            this.weekSelected.next(`${this.article.stock_year}${this.article.stock_week}`);
          }
      } else {
        this.monthList = this.addYear(1, 1, monthList, currentYear + 1);
      }
      this.monthList = this.parseMonthList(monthList);
    }

    this.article.out_of_delivery = this.monthList.length === 0;
  }

  parseMonthList(obj: Object) {
    if (obj) {
      let ownProps = Object.keys(obj),
        i = ownProps.length,
        resArray = new Array(i); // preallocate the Array
      while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

      return resArray;
    } else {
      return [];
    }
  }

  addYear(startWeek, yearDelta, currentList, maxYear?) {
    let monthList = currentList === null ? {} : currentList;
    const currentYear = moment().format("YYYY");

    for (
      let i = startWeek;
      i <= (yearDelta === 0 ? 52 : moment().isoWeek());
      i++
    ) {
      const key = this.applicationService
        .getDateOfWeek(this.article.stock_year + yearDelta, i)
        .format("YYYYMM");

      if (!monthList[key]) {
        monthList[key] = [];
      }

      // Add the week to the month list
      if (
        (this.article.stock_year + yearDelta <= maxYear) &&
        Number(this.article.stock_year + yearDelta) >
          this.currentDate.getFullYear() ||
        (Number(this.article.stock_year) === this.currentDate.getFullYear() &&
          i >= moment().isoWeek())
      ) {
        monthList[key].push({
          value:
            String(this.article.stock_year + yearDelta) +
            this.getConvertedMonth(i),
          month: this.applicationService
            .getDateOfWeek(this.article.stock_year + yearDelta, i)
            .format("MM"),
          text:
            i === moment().isoWeek() &&
            this.article.stock_year + yearDelta === currentYear
              ? this.translateService.instant("DIRECT")
              : i +
                " " +
                (this.article.stock_year + yearDelta) +
                ` (${['WG', 'GHO'].includes(this.applicationService.getSelectCompanyGroupCode()) ? this.translateService.instant('IN_WEEK_OF') + ' ' : ''}` +
                this.applicationService
                  .getDateOfWeek(this.article.stock_year + yearDelta, i)
                  .format("DD-MM-YYYY") +
                ")",
        });
      }
    }
    return monthList;
  }

  getConvertedMonth(weekNumber) {
    const stringMonth = String(weekNumber);
    return stringMonth.length < 2 ? "0" + stringMonth : stringMonth;
  }

  getMonthDescription(month) {
    const monthYear = month.substr(0, 4);
    const monthIndex = Number(month.substr(4, 6));

    return (
      this.translateService.instant(
        `MONTH_${monthIndex < 10 ? "0" + monthIndex : monthIndex}`
      ) +
      " " +
      monthYear
    );
  }

  getMonthDescriptionShort(month) {
    const monthDescription = month.substr(4, 6);
    return monthDescription;
  }

  selectWeek(data) {
    const value = data.target.value;
    
    // Check if "or before" option was selected
    if (value === 'or_before') {
      this.orBeforeSelected = true;
      this.generateOrBeforeWeeksList();
      return;
    }
    
    this.orBeforeSelected = false;
    this.deliveryWeek = value;

    if (this.showAsap && !this.showOrBeforeOption) {
      if (value !== this.monthList[0][1][0].value) {
        this.article.asap_delivery = false;
      } else {
        this.article.asap_delivery = true;
      }
    } else {
      this.article.asap_delivery = false;
    }

    this.weekSelected.next(value);
  }

  generateOrBeforeWeeksList() {
    // Generate weeks from current week until the first available week
    this.orBeforeWeeksList = [];
    const currentWeek = moment().isoWeek();
    const currentYear = moment().year();
    
    // Get the first available week from monthList
    if (this.monthList.length > 0 && this.monthList[0][1]?.length > 0) {
      const firstWeekValue = this.monthList[0][1][0].value;
      const targetYear = parseInt(firstWeekValue.substring(0, 4), 10);
      const targetWeek = parseInt(firstWeekValue.substring(4), 10);
      
      // Generate weeks from current week to target week
      let year = currentYear;
      let week = currentWeek;
      
      while (year < targetYear || (year === targetYear && week <= targetWeek)) {
        const weekStr = week < 10 ? '0' + week : String(week);
        const weekDate = moment().year(year).isoWeek(week).startOf('isoWeek');
        
        this.orBeforeWeeksList.push({
          value: `${year}${weekStr}`,
          text: `${week} ${year} (${weekDate.format('DD-MM-YYYY')})`
        });
        
        week++;
        if (week > 52) {
          week = 1;
          year++;
        }
      }
    }
  }

  selectOrBeforeWeek(data) {
    this.selectedOrBeforeWeek = data.target.value;
    if (this.selectedOrBeforeWeek) {
      this.weekSelected.next(this.selectedOrBeforeWeek);
    }
  }

  weekValue(index, week) {
    return week.value;
  }
}
